import { Injectable, inject } from '@angular/core';
import { Cognito, LoggerService, SessionToken } from '@enliven/angular';
import { Amplify } from 'aws-amplify';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  signIn,
  signOut
} from 'aws-amplify/auth';
import { from, map, Observable } from 'rxjs';
import { authorizationConstants } from '../constants/authorization-constants';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  COGNITO,
  NEXT_ORG_E360_URL,
  SIGN_IN_URL,
  SSO_SIGN_OUT_MSG,
  SSO_USER_SIGNED_IN
} from '../constants/storage-constants';
import { E360SessionService } from './e360-session.service';

@Injectable({
  providedIn: 'root'
})
export class AmplifyService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly logger: LoggerService = inject(LoggerService);

  // Store full Cognito config for SSO-style token refresh via HTTP
  private userPoolUri: string | null = null;
  private cognitoClientId: string | null = null;

  // types of invalid password messages
  private readonly REQUEST_NOT_ALLOWED: string = 'Unable to login because of security reasons.';
  private readonly TEMP_PW_EXPIRED: string = 'Temporary password has expired';
  private readonly USER_IS_DISABLED: string = 'User is disabled';

  // expose amplify library methods
  signIn = signIn;
  signOut = signOut;
  confirmSignIn = confirmSignIn;
  resetPassword = resetPassword;
  confirmResetPassword = confirmResetPassword;
  fetchUserAttributes = fetchUserAttributes;
  // Note: fetchAuthSession is used internally during extractTokensFromAmplifySession()
  // but should not be called after initial authentication to prevent Amplify's automatic refresh

  configureAmplify({ userPoolId, clientId, userPoolUri }: Cognito) {
    // Store config for SSO-style HTTP token refresh
    this.userPoolUri = userPoolUri;
    this.cognitoClientId = clientId;

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId,
          userPoolClientId: clientId
        }
      }
    });
  }

  cleanStorageAfterSignOut() {
    this.e360SessionService.clearAccessTokenExpiryTimer();
    sessionStorage.clear();
    localStorage.removeItem(COGNITO);
    localStorage.removeItem(SSO_USER_SIGNED_IN);
    localStorage.removeItem(SSO_SIGN_OUT_MSG);
    localStorage.removeItem(SIGN_IN_URL);
    localStorage.removeItem(NEXT_ORG_E360_URL);

    // Also clear any remaining Amplify tokens (if any)
    this.clearAmplifyTokensFromStorage();
  }

  handleAmplifyError(err: any, override?: string): string {
    let msg: string = '';
    switch (err.name) {
      case authorizationConstants.userNotConfirmedException:
        msg = 'ERRORS.ENSURE_YOU_HAVE_CONFIRMED_YOUR_ACCOUNT';
        break;
      case authorizationConstants.passwordResetRequiredException:
        msg = 'ERRORS.PASSWORD_RESET_REQUIRED';
        break;
      case authorizationConstants.notAuthorizedException:
        if (err.message.includes(this.TEMP_PW_EXPIRED)) {
          msg = 'ERRORS.TEMP_PASSWORD_EXPIRED';
        } else if (err.message.includes(this.USER_IS_DISABLED)) {
          msg = 'ERRORS.INACTIVE_USER';
        } else if (err.message.includes(this.REQUEST_NOT_ALLOWED)) {
          msg = 'ERRORS.ADAPTIVE_AUTH';
        } else {
          msg = override || 'ERRORS.INCORRECT_USERNAME_OR_PASSWORD';
        }
        break;
      case authorizationConstants.userNotFoundException:
        msg = 'ERRORS.USER_NOT_FOUND';
        break;
      case authorizationConstants.invalidParameterException:
        msg = 'ERRORS.INVALID_PASSWORD';
        break;
      case authorizationConstants.invalidPasswordException:
        msg = 'ERRORS.INVALID_PASSWORD';
        break;
      case authorizationConstants.expiredCodeException:
        msg = 'ERRORS.EXPIRED_CODE';
        break;
      case authorizationConstants.invalidCodeException:
        msg = 'ERRORS.INVALID_CODE';
        break;
      case authorizationConstants.limitExceededException:
        msg = 'ERRORS.FORGOT_PASSWORD_LIMIT_EXCEEDED';
        break;
      default:
        msg = 'ERRORS.ERROR_PROCESSING_REQUEST';
        break;
    }
    return msg;
  }

  handleSuccessfulSignIn() {
    this.e360SessionService.cleanupStorageAfterSignIn(false); // Direct Login (not SSO)
    // Note: Tokens should already be set via extractTokensFromAmplifySession()
  }

  /**
   * Extract tokens from Amplify AuthSession and set them in E360SessionService
   * After extraction, session management transitions to SSO-style HTTP token refresh
   * @returns Observable<SessionToken> for consistent token handling
   */
  extractTokensFromAmplifySession(): Observable<SessionToken> {
    return from(fetchAuthSession()).pipe(
      map(authSession => {
        const { idToken, accessToken } = authSession.tokens;

        // Extract refresh token from Amplify's localStorage
        // Amplify stores it but doesn't expose it via API, so we need to get it manually
        const refreshToken = this.extractRefreshTokenFromAmplifyStorage();

        const tokens: SessionToken = {
          idToken: idToken.toString(),
          accessToken: accessToken.toString(),
          refreshToken: refreshToken // Now we have the refresh token for SSO-style refresh
        };

        // Set tokens in E360SessionService immediately
        // From this point on, we use SSO-style HTTP token refresh, not Amplify
        this.e360SessionService.setSessionTokens(tokens);

        // Clear Amplify's stored tokens now that we have our own copy
        // This prevents Amplify from managing tokens behind the scenes
        this.clearAmplifyTokensFromStorage();

        return tokens;
      })
    );
  }

  /**
   * Refresh tokens using HTTP POST to Cognito token endpoint
   * This is SSO-style token refresh - does NOT use Amplify's fetchAuthSession
   * Delegates to E360SessionService for DRY shared implementation
   * @param refreshToken The refresh token to use for getting new tokens
   * @returns Observable<SessionToken> matching SSO pattern
   */
  refreshTokens(refreshToken: string): Observable<SessionToken> {
    return this.e360SessionService.refreshTokensViaHttp(refreshToken, this.userPoolUri, this.cognitoClientId);
  }

  /**
   * Revoke (invalidate) the refresh token on the Cognito server
   * This prevents the token from being used again, similar to SSO sign-out
   * https://docs.aws.amazon.com/cognito/latest/developerguide/revocation-endpoint.html
   * @param refreshToken The refresh token to revoke
   * @returns Observable<void>
   */
  revokeRefreshToken(refreshToken: string): Observable<void> {
    if (!refreshToken) {
      this.logger.warn('No refresh token available to revoke');
      return from(Promise.resolve());
    }

    const headers = new HttpHeaders().set('content-type', 'application/x-www-form-urlencoded');
    const params = new URLSearchParams();
    params.set('token', refreshToken);
    params.set('client_id', this.cognitoClientId);

    // Cognito's revocation endpoint
    return this.http.post<void>(`${this.userPoolUri}/oauth2/revoke`, params.toString(), { headers }).pipe(
      map(() => {
        this.logger.info('Refresh token successfully revoked on Cognito server');
      })
    );
  }

  /**
   * Extract refresh token from Amplify's localStorage
   * Amplify stores refresh tokens but doesn't expose them via API
   * @private
   */
  private extractRefreshTokenFromAmplifyStorage(): string | undefined {
    const localStorageKeys: string[] = Object.keys(localStorage);
    const refreshTokenKey: string = localStorageKeys.find(k => k.includes('refreshToken'));
    return refreshTokenKey ? localStorage.getItem(refreshTokenKey) : undefined;
  }

  /**
   * Clear Amplify's tokens from localStorage after we've extracted them
   * This prevents Amplify from attempting any automatic token management
   * Amplify stores tokens with keys like: CognitoIdentityServiceProvider.{clientId}.{username}.*
   */
  private clearAmplifyTokensFromStorage(): void {
    const localStorageKeys: string[] = Object.keys(localStorage);
    const amplifyKeys = localStorageKeys.filter(k => k.includes('CognitoIdentityServiceProvider'));

    amplifyKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    if (amplifyKeys.length > 0) {
      this.logger.info(`Cleared ${amplifyKeys.length} Amplify token keys from localStorage`);
    }
  }

  /**
   * @deprecated This method is deprecated. Use extractTokensFromAmplifySession() instead.
   * Kept for backward compatibility during refactoring.
   */
  private mapAmplifyLocalStorageToE360Session() {
    const localStorageKeys: string[] = Object.keys(localStorage);
    const getKey = (keyPart: string, keys: string[]): string => keys.find(k => k.includes(keyPart));
    const idTokenKey: string = getKey('idToken', localStorageKeys);
    const accessTokenKey: string = getKey('accessToken', localStorageKeys);
    const refreshTokenKey: string = getKey('refreshToken', localStorageKeys);
    const idToken = localStorage.getItem(idTokenKey);
    const accessToken = localStorage.getItem(accessTokenKey);
    const refreshToken = localStorage.getItem(refreshTokenKey);
    this.e360SessionService.setSessionTokens({ idToken, accessToken, refreshToken });
  }
}
